import React, { useEffect, useState, useCallback } from 'react';
import { ROLES, SxStyle } from '../../../types';
import AddPlayerModal from './components/player/add-player-modal';
import SquadOverviewHeader from './components/squad/squad-overview-header';
import { useStringsContext } from '../../../contexts/strings';
import RemovePlayerModal from './components/player/remove-player-modal';
import SquadList from './components/squad/squad-list';
import { SquadPlayer } from './types';
import Box from '@mui/material/Box';
import { useHistory } from 'react-router-dom';
import { PlayerState, PLAYER_TABS } from './player-container';
import Loading from '../../loading';
import { useSquad } from './hooks/squad';
import UploadPlayerImagesModal from './components/player/upload-player-images-modal';
import ScenePage from '../../common/scene/scene-page';
import { useUserAuth } from '../../../contexts/auth';

const { REACT_APP_DEFAULT_LEAGUE_SEASON } = process.env;
const CURRENT_YEAR = parseInt(REACT_APP_DEFAULT_LEAGUE_SEASON as string, 10);
const SEASONS = Array.from(Array(4).keys()).map((i) => CURRENT_YEAR - i);

const styles: SxStyle = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    paddingLeft: '8px',
    paddingRight: '8px',
  },
};

interface Props {
  match: any; // TODO fix type
}

function SquadContainer({ match }: Props) {
  const { teamId } = match.params;
  const searchParams = new URLSearchParams(location.search);
  const year = searchParams.get('year');
  const { strings } = useStringsContext();
  const history = useHistory();
  const { fetchSquad } = useSquad();
  // const [seasons, setSeasons] = useState<number[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [loadingPlayers, setLoadingPlayers] = useState(false);
  const [showAddPlayer, setShowAddPlayer] = useState(false);
  const [showRemovePlayer, setShowRemovePlayer] = useState(false);
  const [showCreateSquadModal, setShowCreateSquad] = useState(false);
  const [selectedSquad, setSelectedSquad] = useState<SquadPlayer[] | null>(null);
  const [selectedPlayer, setSelectedPlayer] = useState<SquadPlayer | null>(null);
  const [selectedSeason, setSelectedSeason] = useState<number>(
    year ? parseInt(year, 10) : CURRENT_YEAR
  );

  // const [breadCrumps, setBreadCrumps] = useState<BreadCrumpsList>([teamId]);
  const [showUploadSquadImages, setShowUploadSquadImages] = useState(false);

  // TODO Clean up
  // useEffect(() => {
  //   const getSeasons = async () => {
  //     setLoading(true);
  //     const seasons = [2026, 2025, 2024, 2023, 2011]; // TODO use remote seasons later;
  //     setSeasons(seasons);
  //     if (seasons) {
  //       setSelectedSeason(seasons[0]);
  //       // setBreadCrumps([
  //       //   { text: teamId, to: `/squad/${teamId}` },
  //       //   { text: seasons[0].toString(), to: `/squad/${teamId}` },
  //       // ]);
  //     }
  //   };
  //   getSeasons();
  // }, [teamId]);

  useEffect(() => {
    const getSquad = async () => {
      if (selectedSeason) {
        setLoading(true);
        setError(false);
        try {
          const squad = await fetchSquad(teamId, selectedSeason);
          setSelectedSquad(squad);
        } catch (error) {
          setError(true);
        } finally {
          setLoading(false);
        }
      }
    };
    getSquad();
  }, [selectedSeason, fetchSquad, teamId]);

  const onSelectSeasons = async (season: number) => {
    setSelectedSeason(season);
  };

  const onEditPlayer = (player: SquadPlayer) => {
    history.push(`/squad/${teamId}/player`, {
      player,
      teamId,
      selectedSquad,
      selectedSeason,
    } as PlayerState);
  };

  const onStatsPlayer = (player: SquadPlayer) => {
    history.push(`/squad/${teamId}/player`, {
      player,
      teamId,
      selectedSquad,
      selectedSeason,
      selectedTab: PLAYER_TABS.STATS,
    } as PlayerState);
  };

  const onDeletePlayer = (player: SquadPlayer) => {
    setSelectedPlayer(player);
    setShowRemovePlayer(true);
  };

  const refretchSquad = useCallback(async () => {
    if (selectedSeason) {
      const squad = await fetchSquad(teamId, selectedSeason);
      setSelectedSquad(squad);
    }
  }, [teamId, fetchSquad, selectedSeason]);

  const onLoadingPlayers = useCallback((loading: boolean) => {
    setLoadingPlayers(loading);
  }, []);

  const { isSuperAdmin, hasTeamRole } = useUserAuth();

  const isReadOnly = !isSuperAdmin && hasTeamRole(teamId, ROLES.VIEWER);

  return (
    <ScenePage title={strings.appBar.squad}>
      <Box sx={styles.container}>
        {loading && <Loading />}
        <SquadOverviewHeader
          seasons={SEASONS}
          selectedSeason={selectedSeason}
          disableAddPlayer={!selectedSquad?.length}
          onShowAddplayer={() => setShowAddPlayer(true)}
          onSelectSeason={onSelectSeasons}
          onUploadSquadImages={() => setShowUploadSquadImages(true)}
          loading={loadingPlayers}
          isReadOnly={isReadOnly}
        />
        {!loading && (
          <SquadList
            teamId={teamId}
            selectedSquad={selectedSquad}
            onCreateSquad={() => setShowCreateSquad(true)}
            onEditPlayer={onEditPlayer}
            onStatsPlayer={onStatsPlayer}
            onDeletePlayer={onDeletePlayer}
            loading={loadingPlayers}
            isReadOnly={isReadOnly}
          />
        )}
        {(showAddPlayer || showCreateSquadModal) && selectedSeason && (
          <AddPlayerModal
            open={showAddPlayer || showCreateSquadModal}
            onClose={() => {
              setShowAddPlayer(false);
              setShowCreateSquad(false);
            }}
            season={selectedSeason}
            teamId={teamId}
            selectedSquad={selectedSquad}
            onLoading={onLoadingPlayers}
            onSubmitted={refretchSquad}
          />
        )}
        {showRemovePlayer && selectedPlayer && selectedSeason && (
          <RemovePlayerModal
            open={showRemovePlayer}
            onClose={() => setShowRemovePlayer(false)}
            onRemovedPlayer={refretchSquad}
            player={selectedPlayer}
            season={selectedSeason}
            teamId={teamId}
          />
        )}
        {showUploadSquadImages && selectedSquad && (
          <UploadPlayerImagesModal
            open={showUploadSquadImages}
            onClose={() => setShowUploadSquadImages(false)}
            selectedSquad={selectedSquad}
            onSubmitted={refretchSquad}
            selectedSeason={selectedSeason.toString()}
          />
        )}
      </Box>
    </ScenePage>
  );
}

export default SquadContainer;
